import React from 'react';
import { TypeSectionBanner } from "../../@types/sectionTypes";
import { BANNER_TYPE } from "../../../globals/constants";
import SectionInnerPageBanner from "../../sections/common/banner";
import PolicyLayout from "../../components/PolicyLayout";

const BusinessContinuityPolicyPage = () => {
    const bannerData: TypeSectionBanner = {
        title: "Business Continuity Policy",
        crumb: "Business Continuity",
        image: "/assets/images/banner/1.jpg"
    };

    const sections = [
        { id: "introduction", title: "Introduction" },
        { id: "scope", title: "Scope" },
        { id: "objectives", title: "Objectives" },
        { id: "risk-assessment", title: "Risk Assessment" },
        { id: "recovery-priorities", title: "Recovery Priorities" },
        { id: "emergency-response", title: "Emergency Response" },
        { id: "communication", title: "Communication Plan" },
        { id: "data-backup", title: "Data Backup and Recovery" },
        { id: "alternative-operations", title: "Alternative Operations" },
        { id: "testing", title: "Testing and Maintenance" },
        { id: "roles", title: "Roles and Responsibilities" },
        { id: "contact", title: "Contact Information" }
    ];

    return (
        <div className="business-continuity-policy-page">
            <SectionInnerPageBanner _data={bannerData} _type={BANNER_TYPE.COMPACT} />
            
            <PolicyLayout title="Business Continuity Policy" sections={sections}>

                <div id="introduction" className="section">
                    <h2>1. Introduction</h2>
                    <p>
                        This Business Continuity Policy outlines Anathoth Technologies Ltd's approach to maintaining 
                        essential business operations during and after disruptive events. Our commitment to clients 
                        requires robust continuity planning to ensure minimal service interruption.
                    </p>
                    <p>
                        This policy addresses various scenarios including natural disasters, cyberattacks, power outages, 
                        pandemic situations, and other events that could impact our ability to deliver technology services 
                        to our clients.
                    </p>
                </div>

                <div id="scope" className="section">
                    <h2>2. Scope</h2>
                    <p>This policy covers:</p>
                    <ul>
                        <li>All Anathoth Technologies Ltd operations and services</li>
                        <li>Custom software development projects and ongoing maintenance</li>
                        <li>Web and mobile application development and hosting</li>
                        <li>Payment gateway integrations and financial systems</li>
                        <li>IT consulting and technical support services</li>
                        <li>Cloud infrastructure and data management</li>
                        <li>Client communication and project management systems</li>
                        <li>All employees, contractors, and third-party service providers</li>
                    </ul>
                </div>

                <div id="objectives" className="section">
                    <h2>3. Objectives</h2>
                    
                    <h3>3.1 Primary Objectives</h3>
                    <ul>
                        <li>Protect the health and safety of employees and stakeholders</li>
                        <li>Maintain critical business operations and client services</li>
                        <li>Minimize financial losses and operational downtime</li>
                        <li>Preserve client data integrity and security</li>
                        <li>Maintain regulatory compliance during disruptions</li>
                        <li>Protect company reputation and client relationships</li>
                    </ul>

                    <h3>3.2 Recovery Time Objectives (RTO)</h3>
                    <ul>
                        <li><strong>Critical Systems:</strong> 4 hours maximum downtime</li>
                        <li><strong>Production Applications:</strong> 8 hours maximum downtime</li>
                        <li><strong>Development Environments:</strong> 24 hours maximum downtime</li>
                        <li><strong>Administrative Systems:</strong> 48 hours maximum downtime</li>
                    </ul>

                    <h3>3.3 Recovery Point Objectives (RPO)</h3>
                    <ul>
                        <li><strong>Client Data:</strong> Maximum 1 hour data loss</li>
                        <li><strong>Financial Records:</strong> Maximum 4 hours data loss</li>
                        <li><strong>Development Code:</strong> Maximum 24 hours data loss</li>
                        <li><strong>Administrative Data:</strong> Maximum 48 hours data loss</li>
                    </ul>
                </div>

                <div id="risk-assessment" className="section">
                    <h2>4. Risk Assessment</h2>
                    
                    <h3>4.1 High-Risk Scenarios</h3>
                    <ul>
                        <li><strong>Cyberattacks:</strong> Ransomware, data breaches, DDoS attacks</li>
                        <li><strong>Infrastructure Failure:</strong> Internet outages, power failures, hardware failures</li>
                        <li><strong>Natural Disasters:</strong> Floods, earthquakes, severe weather</li>
                        <li><strong>Pandemic:</strong> Health emergencies requiring remote work</li>
                        <li><strong>Key Personnel Loss:</strong> Illness or departure of critical team members</li>
                    </ul>

                    <h3>4.2 Medium-Risk Scenarios</h3>
                    <ul>
                        <li><strong>Vendor Failures:</strong> Cloud service provider outages</li>
                        <li><strong>Software Failures:</strong> Critical application bugs or corruption</li>
                        <li><strong>Supply Chain Disruption:</strong> Hardware procurement delays</li>
                        <li><strong>Regulatory Changes:</strong> New compliance requirements</li>
                    </ul>

                    <h3>4.3 Low-Risk Scenarios</h3>
                    <ul>
                        <li><strong>Office Damage:</strong> Fire, break-in, equipment theft</li>
                        <li><strong>Transportation Issues:</strong> Limited impact due to remote work capabilities</li>
                        <li><strong>Minor System Outages:</strong> Single service interruptions</li>
                    </ul>
                </div>

                <div id="recovery-priorities" className="section">
                    <h2>5. Recovery Priorities</h2>
                    
                    <h3>5.1 Immediate Priority (0-4 Hours)</h3>
                    <ul>
                        <li>Ensure employee safety and well-being</li>
                        <li>Activate Business Continuity Team</li>
                        <li>Assess situation and establish communication</li>
                        <li>Restore critical client production systems</li>
                        <li>Implement emergency communication protocols</li>
                    </ul>

                    <h3>5.2 Short-term Priority (4-24 Hours)</h3>
                    <ul>
                        <li>Restore client-facing applications and services</li>
                        <li>Re-establish project management and communication tools</li>
                        <li>Implement alternative work arrangements</li>
                        <li>Notify clients of situation and recovery progress</li>
                        <li>Restore payment processing systems</li>
                    </ul>

                    <h3>5.3 Medium-term Priority (24-72 Hours)</h3>
                    <ul>
                        <li>Restore development environments and tools</li>
                        <li>Resume normal project delivery schedules</li>
                        <li>Restore administrative and support systems</li>
                        <li>Conduct damage assessment and recovery planning</li>
                        <li>Implement lessons learned and improvements</li>
                    </ul>
                </div>

                <div id="emergency-response" className="section">
                    <h2>6. Emergency Response</h2>
                    
                    <h3>6.1 Activation Criteria</h3>
                    <p>Business continuity procedures will be activated when:</p>
                    <ul>
                        <li>Primary office facilities become inaccessible</li>
                        <li>Critical systems experience extended outages (&gt;2 hours)</li>
                        <li>Significant portion of workforce becomes unavailable</li>
                        <li>Major security incident affects operations</li>
                        <li>External events threaten business operations</li>
                    </ul>

                    <h3>6.2 Activation Authority</h3>
                    <ul>
                        <li><strong>Primary:</strong> CEO (Ezer Masaba)</li>
                        <li><strong>Secondary:</strong> COO (Vivian Agenorwot)</li>
                        <li><strong>Technical:</strong> CTO (Samuel Kawalya)</li>
                        <li><strong>Emergency:</strong> Any senior management team member</li>
                    </ul>

                    <h3>6.3 Initial Response Actions</h3>
                    <ul>
                        <li>Ensure immediate safety of all personnel</li>
                        <li>Activate the Business Continuity Team</li>
                        <li>Establish emergency communication center</li>
                        <li>Conduct rapid impact assessment</li>
                        <li>Implement immediate containment measures</li>
                        <li>Begin stakeholder notifications</li>
                    </ul>
                </div>

                <div id="communication" className="section">
                    <h2>7. Communication Plan</h2>
                    
                    <h3>7.1 Internal Communication</h3>
                    <ul>
                        <li><strong>Primary:</strong> WhatsApp Business groups for immediate notifications</li>
                        <li><strong>Secondary:</strong> Personal mobile phones and SMS</li>
                        <li><strong>Backup:</strong> Personal email accounts</li>
                        <li><strong>Meeting Point:</strong> Alternative location (to be communicated)</li>
                    </ul>

                    <h3>7.2 Client Communication</h3>
                    <ul>
                        <li><strong>Immediate:</strong> Email to primary client contacts within 2 hours</li>
                        <li><strong>Updates:</strong> Status updates every 4 hours during critical periods</li>
                        <li><strong>Escalation:</strong> Phone calls for high-impact situations</li>
                        <li><strong>Recovery:</strong> Formal notification when services are restored</li>
                    </ul>

                    <h3>7.3 External Communication</h3>
                    <ul>
                        <li>Vendor and supplier notifications</li>
                        <li>Regulatory authority reporting (if required)</li>
                        <li>Insurance company notifications</li>
                        <li>Media relations (if necessary)</li>
                    </ul>
                </div>

                <div id="data-backup" className="section">
                    <h2>8. Data Backup and Recovery</h2>
                    
                    <h3>8.1 Backup Strategy</h3>
                    <ul>
                        <li><strong>Real-time:</strong> Critical client data replicated continuously</li>
                        <li><strong>Daily:</strong> Full system backups of all production environments</li>
                        <li><strong>Weekly:</strong> Complete archive backups stored offsite</li>
                        <li><strong>Monthly:</strong> Long-term retention backups for compliance</li>
                    </ul>

                    <h3>8.2 Backup Locations</h3>
                    <ul>
                        <li>Primary cloud storage: AWS/Azure (geographically distributed)</li>
                        <li>Secondary cloud storage: Different provider for redundancy</li>
                        <li>Local encrypted storage: For immediate recovery needs</li>
                        <li>Offline storage: Air-gapped backups for critical data</li>
                    </ul>

                    <h3>8.3 Recovery Procedures</h3>
                    <ul>
                        <li>Automated recovery for infrastructure and standard applications</li>
                        <li>Manual recovery procedures for custom applications</li>
                        <li>Data integrity verification before resuming operations</li>
                        <li>Rollback procedures if recovery attempts fail</li>
                    </ul>
                </div>

                <div id="alternative-operations" className="section">
                    <h2>9. Alternative Operations</h2>
                    
                    <h3>9.1 Remote Work Capabilities</h3>
                    <ul>
                        <li>All employees equipped with laptops and mobile devices</li>
                        <li>VPN access to all company systems and applications</li>
                        <li>Cloud-based development and collaboration tools</li>
                        <li>Redundant internet connections for key personnel</li>
                    </ul>

                    <h3>9.2 Alternative Infrastructure</h3>
                    <ul>
                        <li>Multi-cloud hosting architecture for system redundancy</li>
                        <li>Backup communication systems (satellite internet if needed)</li>
                        <li>Portable hardware for emergency operations</li>
                        <li>Alternative office space agreements</li>
                    </ul>

                    <h3>9.3 Service Delivery Modifications</h3>
                    <ul>
                        <li>Prioritized client service levels during emergencies</li>
                        <li>Modified project delivery timelines and milestones</li>
                        <li>Emergency support procedures for critical systems</li>
                        <li>Reduced capacity operations with clear client communication</li>
                    </ul>
                </div>

                <div id="testing" className="section">
                    <h2>10. Testing and Maintenance</h2>
                    
                    <h3>10.1 Regular Testing Schedule</h3>
                    <ul>
                        <li><strong>Monthly:</strong> Backup and recovery testing</li>
                        <li><strong>Quarterly:</strong> Communication system testing</li>
                        <li><strong>Semi-annual:</strong> Full business continuity exercise</li>
                        <li><strong>Annual:</strong> Comprehensive disaster recovery simulation</li>
                    </ul>

                    <h3>10.2 Testing Procedures</h3>
                    <ul>
                        <li>Document all testing activities and results</li>
                        <li>Identify gaps and areas for improvement</li>
                        <li>Update procedures based on test outcomes</li>
                        <li>Train team members on updated procedures</li>
                    </ul>

                    <h3>10.3 Plan Maintenance</h3>
                    <ul>
                        <li>Review and update contact information quarterly</li>
                        <li>Update technical procedures when systems change</li>
                        <li>Revise risk assessments annually</li>
                        <li>Incorporate lessons learned from incidents and tests</li>
                    </ul>
                </div>

                <div id="roles" className="section">
                    <h2>11. Roles and Responsibilities</h2>
                    
                    <h3>11.1 Business Continuity Team</h3>
                    <ul>
                        <li><strong>Incident Commander:</strong> CEO (Ezer Masaba)</li>
                        <li><strong>Operations Lead:</strong> COO (Vivian Agenorwot)</li>
                        <li><strong>Technical Lead:</strong> CTO (Samuel Kawalya)</li>
                        <li><strong>HR Lead:</strong> Karen Lusimbo</li>
                        <li><strong>Communications Lead:</strong> Senior team member (designated)</li>
                    </ul>

                    <h3>11.2 Key Responsibilities</h3>
                    <ul>
                        <li><strong>Incident Commander:</strong> Overall coordination and strategic decisions</li>
                        <li><strong>Operations Lead:</strong> Business operations and client relations</li>
                        <li><strong>Technical Lead:</strong> IT systems recovery and technical coordination</li>
                        <li><strong>HR Lead:</strong> Employee safety and workforce coordination</li>
                        <li><strong>Communications Lead:</strong> Stakeholder communications</li>
                    </ul>
                </div>

                <div id="contact" className="section">
                    <h2>12. Contact Information</h2>
                    
                    <h3>Emergency Contacts (24/7)</h3>
                    <ul>
                        <li><strong>CEO:</strong> Ezer Masaba - ezer.masaba@theanathoth.com</li>
                        <li><strong>COO:</strong> Vivian Agenorwot - vivian.agenorwot@theanathoth.com</li>
                        <li><strong>CTO:</strong> Samuel Kawalya - samuel.kawalya@theanathoth.com</li>
                        <li><strong>Emergency Line:</strong> +256 744 666608</li>
                    </ul>

                    <h3>Business Continuity Support</h3>
                    <ul>
                        <li><strong>Email:</strong> emergency@theanathoth.com</li>
                        <li><strong>Regular Hours:</strong> Monday-Friday, 8 AM - 6 PM EAT</li>
                        <li><strong>Office Address:</strong> 1 Kololo Hill Drive, Kampala, Uganda</li>
                    </ul>

                    <p><em>Last updated on 01 January, 2025</em></p>
                </div>
            </PolicyLayout>
        </div>
    );
};

export default BusinessContinuityPolicyPage; 